import { conversazioniDaMostrare } from '@/lib/qsocials/dm';
import type { DmConversation } from '@/types/dm';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * **La sfida ricevuta dentro Amici › Messaggi** (T2-5, lotto B3a): la scelta di QUALI sfide vanno dove. Puro, senza
 * rete: la lettura e' la cache della barra (`useSfideInArrivo`), qui si sceglie soltanto.
 *
 * 🔑 **L'identita' e' `identities.challenger.sub` contro `friendUserId`, e si confronta con `===`.** `friendUserId`
 * e' il sub Logto dell'altro partecipante (QSocials); il `sub` di chi sfida lo manda il BFF (qlash-limulo#173).
 * Sono lo stesso spazio di nomi per costruzione, ma la loro UGUAGLIANZA non si assume: fail-closed, niente carta a
 * un utente sbagliato (condizione b di tma-orcr, 29/9 11:49:40Z). Nessuna normalizzazione (niente `trim`, niente
 * minuscole), nessun abbinamento per nome o per posizione, e un `sub` assente non e' un sub (`challengeOf` lo legge
 * `null`): una sfida senza `sub` non compare ne' nella conversazione ne' fra le righe dell'elenco.
 *
 * ⚠️ **Il RUOLO e' uno solo: `challenger`.** Una sfida diretta ricevuta ha `target` = io (con un `sub`) e `acceptedBy`
 * quando c'e': leggerli al posto di `challenger` metterebbe la carta di MIA sfida nella conversazione con me stesso
 * o con chi l'ha accettata. Lo provano fixture realistiche (`target.sub` = il mio) e i casi con `target.sub` /
 * `acceptedBy.sub` uguale al `friendUserId` e `challenger` diverso ⇒ zero carte (`sfide-messaggi-modello`).
 * ⚠️ E una sfida il cui `challenger.sub` e' il MIO sub non e' una sfida ricevuta: se il BFF sbagliasse la direzione
 * comparirebbe come «Io ti sfido». Si scarta (`mioSub`).
 */
function subDiChiSfida(c: LimuloChallengeWithIdentities): string | null {
  const sub = c.identities.challenger?.sub;
  return typeof sub === 'string' && sub !== '' ? sub : null;
}

/** Le sfide ricevute da QUELLA persona, ancora da rispondere: la carta nella conversazione con lei. */
export function sfideDiAmico(
  vive: readonly LimuloChallengeWithIdentities[],
  friendUserId: string | undefined,
  mioSub: string | null = null,
): LimuloChallengeWithIdentities[] {
  if (friendUserId === undefined) return [];
  return vive.filter((c) => {
    const sub = subDiChiSfida(c);
    return sub === friendUserId && sub !== mioSub;
  });
}

/**
 * Chi ha una conversazione UTILIZZABILE, per `friendUserId`: una riga dell'elenco (`conversazioniDaMostrare`: le
 * aperte e mai scritte non sono una riga) e non chiusa (una chiusa mostra «non siete piu' amici», mai una carta).
 * `null` = l'elenco non e' arrivato (in lettura o in errore): non si sa chi abbia una conversazione, quindi
 * `sfideSenzaConversazione` non sceglie niente — meglio nessuna riga che una riga doppia.
 */
export function amiciConConversazione(elenco: readonly DmConversation[] | undefined): ReadonlySet<string> | null {
  if (elenco === undefined) return null;
  return new Set(
    conversazioniDaMostrare(elenco)
      .filter((c) => c.closed !== true)
      .map((c) => c.friendUserId),
  );
}

/**
 * Le sfide ricevute da chi NON ha una conversazione utilizzabile: compaiono in cima all'elenco, come righe
 * distinte. Quelle di chi ce l'ha non si duplicano (stanno nella sua conversazione), e quelle senza `sub` non si
 * sanno collocare.
 */
export function sfideSenzaConversazione(
  vive: readonly LimuloChallengeWithIdentities[],
  conConversazione: ReadonlySet<string> | null,
  mioSub: string | null = null,
): LimuloChallengeWithIdentities[] {
  if (conConversazione === null) return [];
  return vive.filter((c) => {
    const sub = subDiChiSfida(c);
    return sub !== null && sub !== mioSub && !conConversazione.has(sub);
  });
}

/** Il tab «Sfide» di Amici: dove porta la riga «altre N sfide». Una navigazione, mai una scrittura. */
export const SFIDE_TAB_PERCORSO = '/friends?tab=duels';

/**
 * **Il TETTO: al massimo DUE carte per persona** (decisione del seggio, follow-up 1 di B3a). Il motore ammette fino a 20
 * sfide in attesa dallo stesso `challenger` e la lettura ne porta 20: con tre carte e la tastiera aperta lo scorrimento
 * dei messaggi andrebbe quasi a zero. Le eccedenti non si mostrano: `altre` dice quante, e la riga «altre N sfide» porta al tab.
 */
export const TETTO_CARTE_PER_AMICO = 2;

export interface VoceDm<N> {
  sfida: LimuloChallengeWithIdentities;
  /** Non `null` = la carta non e' piu' offerta (l'ha appena toccata il giocatore): mostra l'esito coi pulsanti spenti. */
  nota: N | null;
}
export interface GruppoDm<N> {
  /** Chi sfida (il suo `sub`); per una sfida senza `sub` (solo un esito) l'id della sfida. */
  chiave: string;
  voci: VoceDm<N>[];
  /** Le sfide ancora offerte che il tetto non mostra. */
  altre: number;
}

/**
 * Raggruppa per chi sfida e applica il tetto. `esiti` sono le carte appena toccate (transitorie: `AVVISO_MS`) e hanno la
 * PRECEDENZA sul posto: il totale mostrato per persona non supera mai il tetto, nemmeno nei secondi dell'esito.
 * L'ordine dei gruppi e' quello di prima apparizione, fra gli esiti e poi le vive.
 */
export function gruppiConTetto<N>(
  esiti: readonly { sfida: LimuloChallengeWithIdentities; nota: N }[],
  vive: readonly LimuloChallengeWithIdentities[],
  tetto: number = TETTO_CARTE_PER_AMICO,
): GruppoDm<N>[] {
  const chiaveDi = (c: LimuloChallengeWithIdentities) => subDiChiSfida(c) ?? c.challengeId;
  const per = new Map<string, { esiti: { sfida: LimuloChallengeWithIdentities; nota: N }[]; vive: LimuloChallengeWithIdentities[] }>();
  const dove = (k: string) => {
    let g = per.get(k);
    if (g === undefined) {
      g = { esiti: [], vive: [] };
      per.set(k, g);
    }
    return g;
  };
  for (const e of esiti) dove(chiaveDi(e.sfida)).esiti.push(e);
  for (const c of vive) dove(chiaveDi(c)).vive.push(c);
  return [...per.entries()].map(([chiave, g]) => {
    const esitiMostrati = g.esiti.slice(0, tetto);
    const vivaMostrate = g.vive.slice(0, Math.max(0, tetto - esitiMostrati.length));
    return {
      chiave,
      voci: [
        ...esitiMostrati.map((e) => ({ sfida: e.sfida, nota: e.nota })),
        ...vivaMostrate.map((sfida) => ({ sfida, nota: null })),
      ],
      altre: g.vive.length - vivaMostrate.length,
    };
  });
}
